import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Rocket, Gamepad2, MessageSquare, Palette, Clock, Users, Trophy, ChevronDown, ChevronUp, Star } from 'lucide-react';
export const KidsAIProgram = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [hoveredModule, setHoveredModule] = useState<number | null>(null);
  const projects = [{
    icon: <Gamepad2 size={48} data-id="element-3422" />,
    title: 'Build Cool Games',
    description: 'Create your own video games with AI characters that learn and adapt!',
    color: '#FF6B6B'
  }, {
    icon: <MessageSquare size={48} data-id="element-3423" />,
    title: 'Smart Chatbots',
    description: 'Design chatbots that can talk, joke, and help with homework!',
    color: '#4ECDC4'
  }, {
    icon: <Palette size={48} data-id="element-3424" />,
    title: 'AI Art & Music',
    description: 'Make amazing art and music using AI tools - no drawing skills needed!',
    color: '#FFE66D'
  }];
  const weeklyProgram = [{
    week: 1,
    title: 'Meet Your AI Friend',
    activities: ['What is AI?', 'Talk to your first chatbot', 'Build a simple AI helper'],
    color: '#667EEA'
  }, {
    week: 2,
    title: 'Create & Play',
    activities: ['Design AI games', 'Make characters that learn', 'Share with friends'],
    color: '#F093FB'
  }, {
    week: 3,
    title: 'Art & Imagination',
    activities: ['AI drawing tools', 'Music creation', 'Story generators'],
    color: '#4FACFE'
  }, {
    week: 4,
    title: 'Show & Tell',
    activities: ['Finish your project', 'Present to parents', 'Get your certificate!'],
    color: '#43E97B'
  }];
  const testimonials = [{
    name: 'Emma, Age 10',
    quote: 'I made a game where the robot learns to jump higher! My friends love it!',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&q=80',
    project: 'AI Jump Game'
  }, {
    name: 'Marcus, Age 12',
    quote: "I built a chatbot that helps me with my math homework. It's like having a smart friend!",
    image: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=400&q=80',
    project: 'Math Helper Bot'
  }, {
    name: 'Zara, Age 9',
    quote: 'The AI made the coolest drawings for my story! I want to be an AI artist now!',
    image: 'https://images.unsplash.com/photo-1534982841079-afde227ada8f?w=400&q=80',
    project: 'AI Story Illustrator'
  }];
  const faqs = [{
    question: 'Does my child need coding experience?',
    answer: "Nope! We start from scratch and make it super fun and easy. If they can use a tablet or computer, they're ready!"
  }, {
    question: 'What equipment do they need?',
    answer: 'Just a computer or tablet with internet. We provide all the AI tools and software - everything is online and easy to use!'
  }, {
    question: 'Is it safe for kids?',
    answer: 'Absolutely! We use kid-safe AI tools, have live instructors monitoring, and all activities are age-appropriate. Plus, we never share personal information.'
  }, {
    question: 'What if they get stuck?',
    answer: 'Our friendly instructors are always there to help! Plus, kids work in small groups so they can help each other too.'
  }, {
    question: 'Can they continue after the 4 weeks?',
    answer: 'Yes! We have advanced programs, clubs, and monthly challenges to keep the learning going!'
  }];
  return <div className="min-h-screen" style={{
    backgroundColor: '#F0F4FF'
  }} data-id="element-3425">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)'
    }} data-id="element-3426">
        {/* Floating Elements */}
        <motion.div className="absolute top-20 left-10 text-6xl" animate={{
        y: [0, -20, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity
      }} data-id="element-3427">
          🤖
        </motion.div>
        <motion.div className="absolute top-40 right-20 text-5xl" animate={{
        y: [0, 20, 0],
        rotate: [0, 10, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity
      }} data-id="element-3428">
          ✨
        </motion.div>
        <motion.div className="absolute bottom-20 left-1/4 text-5xl" animate={{
        y: [0, -15, 0]
      }} transition={{
        duration: 3.5,
        repeat: Infinity
      }} data-id="element-3429">
          🚀
        </motion.div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-3430">
          <div className="max-w-4xl mx-auto text-center" data-id="element-3431">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.5,
            type: 'spring'
          }} className="inline-block mb-6" data-id="element-3432">
              <div className="bg-yellow-300 text-purple-900 px-6 py-2 rounded-full font-bold text-lg" data-id="element-3433">
                🎉 Ages 8-16 • 4-Week Program
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-5xl md:text-7xl font-black mb-6 text-white" style={{
            textShadow: '4px 4px 0px rgba(0,0,0,0.2)'
          }} data-id="element-3434">
              Build Amazing Things with AI! 🌟
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-2xl md:text-3xl mb-8 text-white font-bold" data-id="element-3435">
              Create games, chatbots, and art using the power of AI!
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3436">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-10 py-5 rounded-full font-black text-2xl shadow-2xl" style={{
              backgroundColor: '#FFE66D',
              color: '#764BA2'
            }} data-id="element-3437">
                🚀 Enroll Now!
              </motion.button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-10 py-5 rounded-full font-bold text-xl border-4 border-white text-white" data-id="element-3438">
                Watch Demo Video
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Hero Image Section */}
        <div className="container mx-auto px-4 mt-12" data-id="element-3439">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="max-w-5xl mx-auto" data-id="element-3440">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white" data-id="element-3441">
              <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80" alt="Kids learning AI" className="w-full h-96 object-cover" data-id="element-3442" />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent" data-id="element-3443" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Build Section */}
      <section className="py-20" style={{
      backgroundColor: '#FFFFFF'
    }} data-id="element-3444">
        <div className="container mx-auto px-4" data-id="element-3445">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3446">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#764BA2'
          }} data-id="element-3447">
              What You'll Build 🎨
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3448">
              Amazing projects that you can show your friends and family!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3449">
            {projects.map((project, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} whileHover={{
            scale: 1.05,
            rotate: 2
          }} className="relative p-8 rounded-3xl shadow-xl cursor-pointer" style={{
            backgroundColor: project.color
          }} onMouseEnter={() => setHoveredModule(index)} onMouseLeave={() => setHoveredModule(null)} data-id="element-3450">
                <motion.div animate={{
              rotate: hoveredModule === index ? [0, -10, 10, 0] : 0
            }} transition={{
              duration: 0.5
            }} className="text-white mb-4" data-id="element-3451">
                  {project.icon}
                </motion.div>
                <h3 className="text-3xl font-black mb-3 text-white" data-id="element-3452">
                  {project.title}
                </h3>
                <p className="text-xl text-white font-medium" data-id="element-3453">
                  {project.description}
                </p>
                {hoveredModule === index && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute -top-4 -right-4 bg-yellow-300 rounded-full p-4 shadow-lg" data-id="element-3454">
                    <Sparkles size={32} color="#764BA2" data-id="element-3455" />
                  </motion.div>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works - 4 Week Program */}
      <section className="py-20" style={{
      backgroundColor: '#F0F4FF'
    }} data-id="element-3456">
        <div className="container mx-auto px-4" data-id="element-3457">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3458">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#667EEA'
          }} data-id="element-3459">
              Your 4-Week Adventure 🗓️
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3460">
              Each week is packed with fun projects and new skills!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-3461">
            {weeklyProgram.map((week, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} whileHover={{
            y: -10
          }} className="relative p-6 rounded-3xl shadow-xl bg-white border-4" style={{
            borderColor: week.color
          }} data-id="element-3462">
                <div className="absolute -top-6 left-6 w-12 h-12 rounded-full flex items-center justify-center text-2xl font-black text-white" style={{
              backgroundColor: week.color
            }} data-id="element-3463">
                  {week.week}
                </div>
                <h3 className="text-2xl font-black mb-4 mt-4" style={{
              color: week.color
            }} data-id="element-3464">
                  {week.title}
                </h3>
                <ul className="space-y-2" data-id="element-3465">
                  {week.activities.map((activity, i) => <li key={i} className="flex items-start gap-2 text-gray-700" data-id="element-3466">
                      <span className="text-xl" data-id="element-3467">✓</span>
                      <span className="font-medium" data-id="element-3468">{activity}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Build Your First AI Project Demo */}
      <section className="py-20" style={{
      background: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)'
    }} data-id="element-3469">
        <div className="container mx-auto px-4" data-id="element-3470">
          <div className="max-w-5xl mx-auto" data-id="element-3471">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-12 shadow-2xl" data-id="element-3472">
              <div className="text-center mb-8" data-id="element-3473">
                <h2 className="text-5xl font-black mb-4" style={{
                color: '#667EEA'
              }} data-id="element-3474">
                  Try It Right Now! 🎮
                </h2>
                <p className="text-2xl text-gray-600" data-id="element-3475">
                  Click the robot to see AI in action!
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-8 min-h-96 flex items-center justify-center" data-id="element-3476">
                <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} whileTap={{
                scale: 0.9
              }} className="cursor-pointer text-center" data-id="element-3477">
                  <div className="text-9xl mb-4" data-id="element-3478">🤖</div>
                  <p className="text-2xl font-bold text-purple-700" data-id="element-3479">
                    Click me!
                  </p>
                </motion.div>
              </div>

              <div className="mt-8 text-center" data-id="element-3480">
                <p className="text-xl text-gray-600 mb-4" data-id="element-3481">
                  This is just a tiny preview! In the real program, you'll build
                  way cooler stuff!
                </p>
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="px-8 py-4 rounded-full font-black text-xl text-white" style={{
                backgroundColor: '#FF6B6B'
              }} data-id="element-3482">
                  Start Building Now! 🚀
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20" style={{
      backgroundColor: '#FFFFFF'
    }} data-id="element-3483">
        <div className="container mx-auto px-4" data-id="element-3484">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3485">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#FF6B6B'
          }} data-id="element-3486">
              Kids Love It! ⭐⭐⭐⭐⭐
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3487">
              See what other kids have built!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3488">
            {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} whileHover={{
            y: -10
          }} className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-3xl p-6 shadow-xl" data-id="element-3489">
                <div className="flex items-center gap-1 mb-4" data-id="element-3490">
                  {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="#FFD700" color="#FFD700" data-id="element-3491" />)}
                </div>
                <div className="mb-4" data-id="element-3492">
                  <img src={testimonial.image} alt={testimonial.name} className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto" data-id="element-3493" />
                </div>
                <p className="text-xl font-bold mb-2 text-purple-900" data-id="element-3494">
                  "{testimonial.quote}"
                </p>
                <p className="text-lg font-black text-purple-700" data-id="element-3495">
                  - {testimonial.name}
                </p>
                <div className="mt-4 bg-white rounded-full px-4 py-2 inline-block" data-id="element-3496">
                  <p className="text-sm font-bold text-gray-700" data-id="element-3497">
                    Built: {testimonial.project}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Parent FAQs */}
      <section className="py-20" style={{
      backgroundColor: '#F0F4FF'
    }} data-id="element-3498">
        <div className="container mx-auto px-4" data-id="element-3499">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3500">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#764BA2'
          }} data-id="element-3501">
              Questions? We've Got Answers! 💬
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3502">
              Everything parents want to know
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4" data-id="element-3503">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-3504">
                <button onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)} className="w-full p-6 flex items-center justify-between hover:bg-purple-50 transition-colors" data-id="element-3505">
                  <span className="text-xl font-bold text-left text-purple-900" data-id="element-3506">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: expandedFAQ === index ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-3507">
                    <ChevronDown size={28} color="#764BA2" data-id="element-3508" />
                  </motion.div>
                </button>
                <motion.div initial={false} animate={{
              height: expandedFAQ === index ? 'auto' : 0,
              opacity: expandedFAQ === index ? 1 : 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-3509">
                  <div className="p-6 pt-0 text-lg text-gray-700" data-id="element-3510">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" style={{
      background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)'
    }} data-id="element-3511">
        <div className="container mx-auto px-4" data-id="element-3512">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center" data-id="element-3513">
            <h2 className="text-5xl md:text-7xl font-black mb-6 text-white" data-id="element-3514">
              Ready to Start? 🎉
            </h2>
            <p className="text-3xl mb-8 text-white font-bold" data-id="element-3515">
              Join 500+ kids already building with AI!
            </p>

            <div className="bg-white rounded-3xl p-8 mb-8" data-id="element-3516">
              <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-3517">
                <div data-id="element-3518">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3519">
                    ₦50,000
                  </div>
                  <div className="text-xl text-gray-600" data-id="element-3520">4-Week Program</div>
                </div>
                <div data-id="element-3521">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3522">16</div>
                  <div className="text-xl text-gray-600" data-id="element-3523">Live Sessions</div>
                </div>
                <div data-id="element-3524">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3525">∞</div>
                  <div className="text-xl text-gray-600" data-id="element-3526">Fun & Learning!</div>
                </div>
              </div>
            </div>

            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-16 py-6 rounded-full font-black text-3xl shadow-2xl" style={{
            backgroundColor: '#FFE66D',
            color: '#764BA2'
          }} data-id="element-3527">
              🚀 Enroll Your Child Now!
            </motion.button>

            <p className="text-white text-xl mt-6" data-id="element-3528">
              Limited spots available • Next cohort starts soon!
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
};